<script>
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import i18n from '@/i18n'
import { sharedHelper } from '@/utils'
import { proposalService } from '@/helpers/services/proposal.service'

/**
 * One brief and the proposal drafted from it.
 *
 * Left: what the customer told the bot. Right: the document — generated on a
 * button, edited here (text, programmes, agenda, and the PRICES, which only a
 * person types), previewed as a PDF, and sent by email when somebody decides
 * to. A sent version is frozen; Generate starts the next one.
 */
const BRIEF_ORDER = ['company', 'name', 'phone', 'email', 'pax', 'audience', 'objectives', 'programme_interest', 'preferred_dates', 'location', 'budget_range', 'hrd_corp']

export default {
  components: { Layout, PageHeader },
  page() {
    return { title: this.leadRef }
  },
  data() {
    return {
      loading: true,
      error: '',
      brand: '',
      lead: null,
      proposal: null,
      versions: [],
      /** Editable copy of proposal.content; lists are edited as one item per line. */
      doc: null,
      generating: false,
      saving: false,
      previewing: false,
      sending: false,
      sendOpen: false,
      sendTo: '',
      sendMessage: '',
      evidence: [],
      /** The brand's catalogue — what sales can add to the proposal. */
      catalogue: [],
      pickId: null,
    }
  },
  computed: {
    leadRef() {
      return this.$route.params.leadRef
    },
    breadcrumbItems() {
      return [
        { text: i18n.t('module.admin'), href: '/' },
        { text: i18n.t('module.proposal'), href: '/proposals' },
        { text: this.leadRef, active: true },
      ]
    },
    briefRows() {
      const f = (this.lead && this.lead.fields) || {}
      const keys = [...BRIEF_ORDER.filter((k) => f[k] !== undefined && f[k] !== ''), ...Object.keys(f).filter((k) => !BRIEF_ORDER.includes(k) && !k.startsWith('_'))]
      return keys.map((k) => ({ key: k, label: this.$t('proposal.field_' + k) !== 'proposal.field_' + k ? this.$t('proposal.field_' + k) : k, value: String(f[k]) }))
    },
    status() {
      return (this.proposal && this.proposal.status) || null
    },
    locked() {
      return this.status === 'sent'
    },
    catalogueOptions() {
      const byCat = {}
      for (const c of this.catalogue) (byCat[c.category || '—'] = byCat[c.category || '—'] || []).push(c)
      const groups = Object.keys(byCat).sort().map((cat) => ({ label: cat, options: byCat[cat].map((c) => ({ value: c.id, text: c.title })) }))
      return [{ value: null, text: this.$t('proposal.pickProgramme') }, ...groups]
    },
    total() {
      if (!this.doc) return null
      let any = false, sum = 0
      for (const r of this.doc.investment.rows) {
        const q = Number(r.quantity), p = r.price === '' || r.price === null ? NaN : Number(r.price)
        if (!isNaN(q) && !isNaN(p)) { any = true; sum += q * p }
      }
      return any ? sum : null
    },
  },
  async created() {
    await this.load()
  },
  methods: {
    async load() {
      this.loading = true
      this.error = ''
      try {
        const res = await proposalService.getProposal(this.leadRef)
        const d = (res && res.data) || {}
        this.brand = d.brand || ''
        this.lead = d.lead || null
        this.proposal = d.proposal || null
        this.versions = d.versions || []
        this.doc = this.proposal ? this.toDoc(this.proposal.content) : null
        const f = (this.lead && this.lead.fields) || {}
        if (!this.sendTo && f.email) this.sendTo = String(f.email)
        if (this.lead && this.lead.tenantId && !this.catalogue.length) {
          const cat = await proposalService.getCatalogue(this.lead.tenantId).catch(() => null)
          this.catalogue = (cat && cat.data) || []
        }
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },
    messageOf(e) {
      return (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) || (e && e.message) || String(e)
    },
    // ── content <-> editor ──────────────────────────────────────────────
    toDoc(c) {
      const lines = (a) => (a || []).join('\n')
      return {
        title: c.title || '',
        prepared_for: { company: (c.prepared_for && c.prepared_for.company) || '', contact_name: (c.prepared_for && c.prepared_for.contact_name) || '' },
        executive_summary: c.executive_summary || '',
        understanding: lines(c.understanding),
        objectives: lines(c.objectives),
        recommended_programmes: (c.recommended_programmes || []).map((p) => ({ ...p })),
        agenda: (c.agenda || []).map((a) => ({ ...a })),
        logistics: c.logistics || '',
        hrd_corp: c.hrd_corp || '',
        investment: {
          rows: ((c.investment && c.investment.rows) || []).map((r) => ({ ...r, price: r.price === null || r.price === undefined ? '' : r.price })),
          note: (c.investment && c.investment.note) || '',
        },
        next_steps: lines(c.next_steps),
        assumptions: lines(c.assumptions),
      }
    },
    fromDoc(d) {
      const list = (s) => String(s || '').split('\n').map((x) => x.trim()).filter(Boolean)
      return {
        title: d.title.trim(),
        prepared_for: { company: d.prepared_for.company.trim(), contact_name: d.prepared_for.contact_name.trim() },
        executive_summary: d.executive_summary.trim(),
        understanding: list(d.understanding),
        objectives: list(d.objectives),
        recommended_programmes: d.recommended_programmes.map((p) => ({ title: p.title || '', catalogue_id: p.catalogue_id || null, why_it_fits: p.why_it_fits || '', duration: p.duration || '', format: p.format || '', audience: p.audience || '' })),
        agenda: d.agenda.map((a) => ({ block: a.block || '', duration: a.duration || '', content: a.content || '' })),
        logistics: d.logistics.trim(),
        hrd_corp: d.hrd_corp.trim(),
        investment: {
          rows: d.investment.rows.map((r) => ({ item: r.item || '', quantity: String(r.quantity || ''), unit: r.unit || '', price: r.price === '' || r.price === null || isNaN(Number(r.price)) ? null : Number(r.price) })),
          note: d.investment.note.trim(),
        },
        next_steps: list(d.next_steps),
        assumptions: list(d.assumptions),
      }
    },
    addProgramme() { this.doc.recommended_programmes.push({ title: '', catalogue_id: null, why_it_fits: '', duration: '', format: '', audience: '' }) },
    /** Add a programme from the catalogue: title and id come from the entry,
     *  the description seeds "why it fits" for sales to sharpen. */
    addFromCatalogue(id) {
      const c = this.catalogue.find((x) => x.id === id)
      if (!c) return
      this.doc.recommended_programmes.push({ title: c.title, catalogue_id: c.id, why_it_fits: c.description || '', duration: '', format: '', audience: '' })
      this.pickId = null
    },
    addAgenda() { this.doc.agenda.push({ block: '', duration: '', content: '' }) },
    addRow() { this.doc.investment.rows.push({ item: '', quantity: '1', unit: '', price: '' }) },
    removeAt(arr, i) { arr.splice(i, 1) },
    // ── actions ─────────────────────────────────────────────────────────
    /**
     * The inbox keeps this lead's card in another tab. A localStorage write is
     * the cheapest cross-tab signal there is: the `storage` event fires in
     * every OTHER tab of this origin, and the inbox reloads its Proposal pane.
     */
    announceChange() {
      try { localStorage.setItem('proposals:changed', JSON.stringify({ leadRef: this.leadRef, at: Date.now() })) } catch (e) { /* storage may be unavailable */ }
    },
    async generate() {
      // A regenerate throws away edits and prices typed into the current
      // version (a new version is created), so it asks once.
      if (this.proposal) {
        const ok = await sharedHelper.confirmQuestion({
          title: i18n.t('proposal.regenerateTitle'),
          text: i18n.t('proposal.regenerateText', [this.proposal.version]),
          confirmText: i18n.t('proposal.regenerateYes'),
        })
        if (!ok) return
      }
      this.generating = true
      this.error = ''
      try {
        const res = await proposalService.generate(this.leadRef)
        this.evidence = (res && res.evidence) || []
        await this.load()
        this.announceChange()
        sharedHelper.alertSuccessMessage(i18n.t('proposal.generated'))
      } catch (e) {
        this.error = this.messageOf(e)
        sharedHelper.alertErrorMessageWithoutCode(this.error)
      } finally {
        this.generating = false
      }
    },
    async save(status) {
      if (!this.doc || this.locked) return
      this.saving = true
      this.error = ''
      try {
        const res = await proposalService.save(this.leadRef, this.fromDoc(this.doc), status)
        this.proposal = (res && res.data) || this.proposal
        this.doc = this.toDoc(this.proposal.content)
        await this.load()
        this.announceChange()
        sharedHelper.alertSuccessMessage(i18n.t(status === 'approved' ? 'proposal.approved' : 'proposal.saved'))
      } catch (e) {
        this.error = this.messageOf(e)
        sharedHelper.alertErrorMessageWithoutCode(this.error)
      } finally {
        this.saving = false
      }
    },
    async preview() {
      this.previewing = true
      try {
        // Save first so the PDF shows what is on screen, not what was last saved.
        if (!this.locked) await proposalService.save(this.leadRef, this.fromDoc(this.doc), null)
        const blob = await proposalService.pdfBlob(this.leadRef)
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.messageOf(e))
      } finally {
        this.previewing = false
      }
    },
    async send() {
      if (!this.sendTo) return
      this.sending = true
      try {
        if (!this.locked) await proposalService.save(this.leadRef, this.fromDoc(this.doc), 'approved')
        const res = await proposalService.send(this.leadRef, this.sendTo, this.sendMessage)
        this.sendOpen = false
        await this.load()
        this.announceChange()
        const mailer = res && res.mailer
        sharedHelper.alertSuccessMessage(mailer === 'log' ? i18n.t('proposal.sentLogOnly', [this.sendTo]) : i18n.t('proposal.sent', [this.sendTo]))
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.messageOf(e))
      } finally {
        this.sending = false
      }
    },
    statusVariant(s) {
      return { draft: 'warning', approved: 'info', sent: 'success' }[s] || 'light'
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="lead && lead.fields && lead.fields.company ? lead.fields.company : leadRef" :items="breadcrumbItems" />

    <div v-if="loading" class="text-muted small">{{ $t('label.loading') }}</div>
    <div v-else-if="!lead" class="alert alert-warning">{{ error || $t('proposal.notFound') }}</div>

    <b-row v-else>
      <!-- ── the brief ─────────────────────────────────────────────────── -->
      <b-col cols="12" lg="4">
        <div class="card">
          <div class="card-header bg-white py-2 d-flex justify-content-between align-items-center">
            <h6 class="mb-0">{{ $t('proposal.brief') }}</h6>
            <small class="text-muted">{{ brand }} · <code>{{ leadRef }}</code></small>
          </div>
          <div class="card-body py-2">
            <table class="table table-sm mb-0">
              <tr v-for="r in briefRows" :key="r.key">
                <td class="text-muted small" style="width:42%">{{ r.label }}</td>
                <td class="small">{{ r.value }}</td>
              </tr>
            </table>
            <small class="text-muted d-block mt-2">{{ $t('proposal.briefHint') }}</small>
          </div>
        </div>

        <div class="card">
          <div class="card-header bg-white py-2"><h6 class="mb-0">{{ $t('proposal.versions') }}</h6></div>
          <div class="card-body py-2 small">
            <div v-if="!versions.length" class="text-muted">{{ $t('proposal.noVersions') }}</div>
            <div v-for="v in versions" :key="v.version" class="d-flex justify-content-between py-1 border-bottom">
              <span>v{{ v.version }}</span>
              <span><b-badge :variant="statusVariant(v.status)">{{ v.status }}</b-badge>
                <span class="text-muted ml-2">{{ (v.sentAt || v.createdAt || '').toString().replace('T',' ').slice(0,16) }}</span></span>
            </div>
          </div>
        </div>
      </b-col>

      <!-- ── the proposal ──────────────────────────────────────────────── -->
      <b-col cols="12" lg="8">
        <div class="card">
          <div class="card-header bg-white py-2 d-flex flex-wrap justify-content-between align-items-center" style="gap:8px">
            <h6 class="mb-0">
              {{ $t('proposal.title') }}
              <b-badge v-if="status" :variant="statusVariant(status)" class="ml-2">v{{ proposal.version }} · {{ status }}</b-badge>
            </h6>
            <div class="d-flex flex-wrap" style="gap:6px">
              <b-button size="sm" :variant="proposal ? 'outline-secondary' : 'primary'" :disabled="generating" @click="generate">
                <i class="mdi mdi-auto-fix mr-1"></i>{{ generating ? $t('proposal.generating') : (proposal ? $t('proposal.regenerate') : $t('proposal.generate')) }}
              </b-button>
              <template v-if="doc">
                <b-button size="sm" variant="outline-secondary" :disabled="saving || locked" @click="save(null)">{{ $t('proposal.save') }}</b-button>
                <b-button size="sm" variant="outline-info" :disabled="saving || locked || status === 'approved'" @click="save('approved')">{{ $t('proposal.approve') }}</b-button>
                <b-button size="sm" variant="outline-primary" :disabled="previewing" @click="preview"><i class="mdi mdi-file-pdf-box mr-1"></i>{{ previewing ? $t('label.loading') : $t('proposal.previewPdf') }}</b-button>
                <b-button size="sm" variant="primary" :disabled="locked" @click="sendOpen = !sendOpen"><i class="mdi mdi-send mr-1"></i>{{ $t('proposal.send') }}</b-button>
              </template>
            </div>
          </div>

          <div class="card-body">
            <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

            <div v-if="!doc" class="text-muted">
              <p>{{ $t('proposal.noneYet') }}</p>
              <p class="small mb-0">{{ $t('proposal.generateHint') }}</p>
            </div>

            <template v-else>
              <div v-if="locked" class="alert alert-success py-2 small">
                {{ $t('proposal.lockedNote', [proposal.sentTo || '', (proposal.sentAt || '').toString().replace('T',' ').slice(0,16)]) }}
              </div>

              <!-- send panel -->
              <div v-if="sendOpen && !locked" class="border rounded p-3 mb-3" style="background:var(--light, #f8f9fa)">
                <h6 class="mb-2">{{ $t('proposal.sendTitle') }}</h6>
                <p class="small text-muted mb-2">{{ $t('proposal.sendHint') }}</p>
                <b-row>
                  <b-col cols="12" lg="5">
                    <b-form-group :label="$t('proposal.sendTo')"><b-form-input v-model="sendTo" type="email" /></b-form-group>
                  </b-col>
                  <b-col cols="12" lg="7">
                    <b-form-group :label="$t('proposal.sendMessage')"><b-form-textarea v-model="sendMessage" rows="2" :placeholder="$t('proposal.sendMessagePlaceholder')" /></b-form-group>
                  </b-col>
                </b-row>
                <div class="d-flex justify-content-end" style="gap:6px">
                  <b-button size="sm" variant="light" @click="sendOpen = false">{{ $t('label.cancel') }}</b-button>
                  <b-button size="sm" variant="primary" :disabled="sending || !sendTo" @click="send">{{ sending ? $t('label.loading') : $t('proposal.sendConfirm') }}</b-button>
                </div>
              </div>

              <fieldset :disabled="locked">
                <b-form-group :label="$t('proposal.docTitle')"><b-form-input v-model="doc.title" /></b-form-group>
                <b-row>
                  <b-col lg="6"><b-form-group :label="$t('proposal.field_company')"><b-form-input v-model="doc.prepared_for.company" /></b-form-group></b-col>
                  <b-col lg="6"><b-form-group :label="$t('proposal.field_name')"><b-form-input v-model="doc.prepared_for.contact_name" /></b-form-group></b-col>
                </b-row>
                <b-form-group :label="$t('proposal.summary')"><b-form-textarea v-model="doc.executive_summary" rows="4" /></b-form-group>
                <b-row>
                  <b-col lg="6"><b-form-group :label="$t('proposal.understanding')" :description="$t('proposal.onePerLine')"><b-form-textarea v-model="doc.understanding" rows="5" /></b-form-group></b-col>
                  <b-col lg="6"><b-form-group :label="$t('proposal.objectives')" :description="$t('proposal.onePerLine')"><b-form-textarea v-model="doc.objectives" rows="5" /></b-form-group></b-col>
                </b-row>

                <h6 class="mt-3">{{ $t('proposal.programmes') }}</h6>
                <div v-for="(p, i) in doc.recommended_programmes" :key="'p' + i" class="border rounded p-2 mb-2">
                  <b-row>
                    <b-col lg="5"><b-form-input v-model="p.title" size="sm" :placeholder="$t('proposal.progTitle')" /></b-col>
                    <b-col lg="2"><b-form-input v-model="p.duration" size="sm" :placeholder="$t('proposal.progDuration')" /></b-col>
                    <b-col lg="2"><b-form-input v-model="p.format" size="sm" :placeholder="$t('proposal.progFormat')" /></b-col>
                    <b-col lg="2"><b-form-input v-model="p.audience" size="sm" :placeholder="$t('proposal.progAudience')" /></b-col>
                    <b-col lg="1" class="text-right"><b-button size="sm" variant="light" @click="removeAt(doc.recommended_programmes, i)"><i class="mdi mdi-close"></i></b-button></b-col>
                  </b-row>
                  <b-form-textarea v-model="p.why_it_fits" size="sm" rows="2" class="mt-2" :placeholder="$t('proposal.progWhy')" />
                  <small v-if="p.catalogue_id" class="text-muted">catalogue: <code>{{ p.catalogue_id }}</code></small>
                </div>
                <div class="d-flex flex-wrap align-items-center" style="gap:8px">
                  <b-form-select v-if="catalogue.length" v-model="pickId" :options="catalogueOptions" size="sm" style="max-width:420px" @change="addFromCatalogue" />
                  <b-button size="sm" variant="outline-secondary" @click="addProgramme"><i class="mdi mdi-plus mr-1"></i>{{ $t('proposal.addProgramme') }}</b-button>
                  <small v-if="catalogue.length" class="text-muted">{{ $t('proposal.catalogueHint', [catalogue.length]) }}</small>
                </div>

                <h6 class="mt-4">{{ $t('proposal.agenda') }}</h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead><tr class="small text-muted"><th style="width:30%">{{ $t('proposal.agBlock') }}</th><th style="width:14%">{{ $t('proposal.agDuration') }}</th><th>{{ $t('proposal.agContent') }}</th><th style="width:3rem"></th></tr></thead>
                    <tbody>
                      <tr v-for="(a, i) in doc.agenda" :key="'a' + i">
                        <td><b-form-input v-model="a.block" size="sm" /></td>
                        <td><b-form-input v-model="a.duration" size="sm" /></td>
                        <td><b-form-input v-model="a.content" size="sm" /></td>
                        <td><b-button size="sm" variant="light" @click="removeAt(doc.agenda, i)"><i class="mdi mdi-close"></i></b-button></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <b-button size="sm" variant="outline-secondary" @click="addAgenda"><i class="mdi mdi-plus mr-1"></i>{{ $t('proposal.addAgenda') }}</b-button>

                <b-row class="mt-4">
                  <b-col lg="6"><b-form-group :label="$t('proposal.logistics')"><b-form-textarea v-model="doc.logistics" rows="3" /></b-form-group></b-col>
                  <b-col lg="6"><b-form-group :label="$t('proposal.hrdCorp')"><b-form-textarea v-model="doc.hrd_corp" rows="3" /></b-form-group></b-col>
                </b-row>

                <h6 class="mt-2">{{ $t('proposal.investment') }} <small class="text-muted font-weight-normal ml-2">{{ $t('proposal.investmentHint') }}</small></h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead><tr class="small text-muted"><th>{{ $t('proposal.invItem') }}</th><th style="width:10%">{{ $t('proposal.invQty') }}</th><th style="width:14%">{{ $t('proposal.invUnit') }}</th><th style="width:16%" class="text-right">{{ $t('proposal.invPrice') }}</th><th style="width:16%" class="text-right">{{ $t('proposal.invAmount') }}</th><th style="width:3rem"></th></tr></thead>
                    <tbody>
                      <tr v-for="(r, i) in doc.investment.rows" :key="'r' + i">
                        <td><b-form-input v-model="r.item" size="sm" /></td>
                        <td><b-form-input v-model="r.quantity" size="sm" /></td>
                        <td><b-form-input v-model="r.unit" size="sm" /></td>
                        <td><b-form-input v-model="r.price" size="sm" type="number" min="0" step="0.01" class="text-right" :placeholder="$t('proposal.invPricePlaceholder')" /></td>
                        <td class="text-right small pt-2">{{ (!isNaN(Number(r.quantity)) && r.price !== '' && !isNaN(Number(r.price))) ? (Number(r.quantity) * Number(r.price)).toLocaleString('en-MY', { minimumFractionDigits: 2 }) : '—' }}</td>
                        <td><b-button size="sm" variant="light" @click="removeAt(doc.investment.rows, i)"><i class="mdi mdi-close"></i></b-button></td>
                      </tr>
                    </tbody>
                    <tfoot v-if="total !== null"><tr><td colspan="4" class="text-right font-weight-bold">{{ $t('proposal.invTotal') }}</td><td class="text-right font-weight-bold">RM {{ total.toLocaleString('en-MY', { minimumFractionDigits: 2 }) }}</td><td></td></tr></tfoot>
                  </table>
                </div>
                <b-button size="sm" variant="outline-secondary" @click="addRow"><i class="mdi mdi-plus mr-1"></i>{{ $t('proposal.addRow') }}</b-button>
                <b-form-group :label="$t('proposal.invNote')" class="mt-3"><b-form-input v-model="doc.investment.note" /></b-form-group>

                <b-row>
                  <b-col lg="6"><b-form-group :label="$t('proposal.nextSteps')" :description="$t('proposal.onePerLine')"><b-form-textarea v-model="doc.next_steps" rows="4" /></b-form-group></b-col>
                  <b-col lg="6"><b-form-group :label="$t('proposal.assumptions')" :description="$t('proposal.onePerLine')"><b-form-textarea v-model="doc.assumptions" rows="4" /></b-form-group></b-col>
                </b-row>
              </fieldset>

              <div v-if="!locked" class="d-flex justify-content-end mt-2" style="gap:6px">
                <b-button variant="outline-secondary" :disabled="saving" @click="save(null)">{{ saving ? $t('label.loading') : $t('proposal.save') }}</b-button>
                <b-button variant="primary" :disabled="previewing" @click="preview">{{ $t('proposal.previewPdf') }}</b-button>
              </div>
            </template>
          </div>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
